import React, { useEffect, useState } from "react";
import "./styles/dashboard.css";
import toast from "react-hot-toast";
import CommonHeader from "./components/CommonHeader";

const VehicleManagement = () => {
  const [vehicles, setVehicles] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [formData, setFormData] = useState({
    vehicle_name: ""
  });
  const [loading, setLoading] = useState(false);

  const fetchVehicles = async () => {
    setLoading(true);
    try {
      const data = new URLSearchParams();
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/VehicleLookup/getAllVehicles`, {
        method: "POST",
        headers: {
          Authorization: localStorage.getItem("token"),
        },
        body: data,
      });

      const result = await response.json();
      if (result.status === 1) {
        setVehicles(result.vehicles || []);
      } else {
        toast.error(result.message || "Failed to fetch vehicles");
      }
    } catch (error) {
      console.error("Error fetching vehicles:", error);
      toast.error("Failed to fetch vehicles");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVehicles();
  }, []);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.vehicle_name.trim()) {
      toast.error("Vehicle name is required");
      return;
    }

    setLoading(true);
    try {
      const endpoint = editingVehicle 
        ? `${process.env.REACT_APP_API_URL}/VehicleLookup/updateVehicle`
        : `${process.env.REACT_APP_API_URL}/VehicleLookup/createVehicle`;

      const data = new URLSearchParams();
      if (editingVehicle) {
        data.append('vehicle_id', editingVehicle.vehicle_lookup_id);
      }
      data.append('vehicle_name', formData.vehicle_name);

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          Authorization: localStorage.getItem("token"),
        },
        body: data,
      });

      const result = await response.json();
      if (result.status === 1) {
        toast.success(result.message);
        setShowModal(false);
        setFormData({ vehicle_name: "" });
        setEditingVehicle(null);
        fetchVehicles();
      } else {
        toast.error(result.message || "Operation failed");
      }
    } catch (error) {
      console.error("Error saving vehicle:", error);
      toast.error("Failed to save vehicle");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (vehicle) => {
    setEditingVehicle(vehicle);
    setFormData({ vehicle_name: vehicle.vehicle_name });
    setShowModal(true);
  };

  const handleDelete = async (vehicleId, vehicleName) => {
    if (window.confirm(`Are you sure you want to delete vehicle "${vehicleName}"?`) === false) {
      return;
    }

    setLoading(true);
    try {
      const data = new URLSearchParams();
      data.append('vehicle_id', vehicleId);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/VehicleLookup/deleteVehicle`, {
        method: "POST",
        headers: {
          Authorization: localStorage.getItem("token"),
        },
        body: data,
      });

      const result = await response.json();
      if (result.status === 1) {
        toast.success(result.message);
        fetchVehicles();
      } else {
        toast.error(result.message || "Failed to delete vehicle");
      }
    } catch (error) {
      console.error("Error deleting vehicle:", error);
      toast.error("Failed to delete vehicle");
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (vehicleId, vehicleName) => {
    if (window.confirm(`Are you sure you want to restore vehicle "${vehicleName}"?`) === false) {
      return;
    }

    setLoading(true);
    try {
      const data = new URLSearchParams();
      data.append('vehicle_id', vehicleId);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/VehicleLookup/restoreVehicle`, {
        method: "POST",
        headers: {
          Authorization: localStorage.getItem("token"),
        },
        body: data,
      });

      const result = await response.json();
      if (result.status === 1) {
        toast.success(result.message);
        fetchVehicles();
      } else {
        toast.error(result.message || "Failed to restore vehicle");
      }
    } catch (error) {
      console.error("Error restoring vehicle:", error);
      toast.error("Failed to restore vehicle");
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingVehicle(null);
    setFormData({ vehicle_name: "" });
  };

  return (
    <>
      <div class="d-flex flex-column flex-lg-row h-lg-full bg-surface-secondary">
        <div class="h-screen flex-grow-1 overflow-y-lg-auto">
          <CommonHeader />

          <main class="py-6 bg-surface-secondary">
            <div class="container-fluid">
              <div class="card shadow border-0 mb-7">
                <div class="card-header">
                  <h5 class="mb-0">Vehicle Management</h5>
                </div>
                <div class="table-responsive">
                  <table class="table table-hover table-nowrap">
                    <thead class="thead-light">
                      <tr>
                        <th scope="col">Vehicle ID</th>
                        <th scope="col">Vehicle Name</th>
                        <th scope="col">Status</th>
                        <th scope="col">Modified On</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colspan="5" class="text-center">
                            <div class="spinner-border" role="status">
                              <span class="visually-hidden">Loading...</span>
                            </div>
                          </td>
                        </tr>
                      ) : vehicles.length > 0 ? (
                        vehicles.map((vehicle) => (
                          <tr key={vehicle.vehicle_lookup_id}>
                            <td>{vehicle.vehicle_lookup_id}</td>
                            <td>{vehicle.vehicle_name}</td>
                            <td>
                              <span class="badge badge-lg badge-dot">
                                {vehicle.status == 0 ? (
                                  <>
                                    <i style={{ backgroundColor: "gray" }}></i>
                                    Inactive
                                  </>
                                ) : (
                                  <>
                                    <i className="bg-success"></i>
                                    Active
                                  </>
                                )}
                              </span>
                            </td>
                            <td>{new Date(vehicle.mod_on).toLocaleDateString()}</td>
                            <td class="text-end">
                              <button
                                onClick={() => handleEdit(vehicle)}
                                class="btn btn-sm btn-neutral"
                                style={{ marginRight: "0.5rem" }}
                              >
                                Edit
                              </button>
                              {vehicle.status == 1 ? (
                                <button
                                  type="button"
                                  class="btn btn-sm btn-square btn-neutral text-danger-hover"
                                  onClick={() => handleDelete(vehicle.vehicle_lookup_id, vehicle.vehicle_name)}
                                  title="Delete Vehicle"
                                >
                                  <i class="bi bi-trash"></i>
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  class="btn btn-sm btn-square btn-neutral text-success-hover"
                                  onClick={() => handleRestore(vehicle.vehicle_lookup_id, vehicle.vehicle_name)}
                                  title="Restore Vehicle"
                                >
                                  <i class="bi bi-arrow-clockwise"></i>
                                </button>
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colspan="5" class="text-center text-muted">No vehicles found</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div class="card-footer border-0 py-5">
                  <button
                    class="btn d-inline-flex btn-sm btn-primary mx-1"
                    onClick={() => setShowModal(true)}
                  >
                    <span class="pe-2">
                      <i class="bi bi-plus"></i>
                    </span>
                    <span>Add Vehicle</span>
                  </button>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>

      {/* Add/Edit Vehicle Modal */}
      {showModal && (
        <div class="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div class="modal-dialog">
            <div class="modal-content">
              <div class="modal-header">
                <h5 class="modal-title">
                  {editingVehicle ? 'Edit Vehicle' : 'Add New Vehicle'}
                </h5>
                <button 
                  type="button" 
                  class="btn-close" 
                  onClick={closeModal}
                ></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div class="modal-body">
                  <div class="mb-3">
                    <label for="vehicle_name" class="form-label">Vehicle Name</label>
                    <input
                      type="text"
                      class="form-control"
                      id="vehicle_name"
                      name="vehicle_name"
                      value={formData.vehicle_name}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter vehicle name (e.g., Bus-001)"
                    />
                  </div>
                </div>
                <div class="modal-footer">
                  <button 
                    type="button" 
                    class="btn btn-secondary" 
                    onClick={closeModal}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    class="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span class="spinner-border spinner-border-sm me-2" role="status"></span>
                        Saving...
                      </>
                    ) : (
                      editingVehicle ? 'Update Vehicle' : 'Add Vehicle'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VehicleManagement;
